import React from 'react';
import ResizeBoxesStep from '../shared/ResizeBoxesStep';
import ScaleStep from '../shared/ScaleStep';
import ShowScaleContoursStep from '../shared/ShowScaleContoursStep';
import StatusStep from '../shared/StatusStep';
import TagsStep from '../shared/TagsStep';
import WizardShell from '../shared/WizardShell';
import { figureUpdateInputs, parseIntOrNull } from '../shared/wizardUtils';
import useWizardFlow from '../shared/useWizardFlow';
import ArrowStep from './ArrowStep';
import DataStep from './DataStep';
import ShowContoursStep from './ShowContoursStep';
import SkeletonStep from './SkeletonStep';
import SiteStep from './SiteStep';

const STEP_ORDER = [
  'data',
  'site',
  'tags',
  'resize_boxes',
  'show_contours',
  'scale',
  'show_scale_contours',
  'arrow',
  'skeleton',
  'status'
];
const STEP_LABELS = {
  data: 'Basic Data',
  site: 'Site',
  tags: 'Tags',
  resize_boxes: 'Resize Boxes',
  show_contours: 'Contours',
  scale: 'Scale',
  show_scale_contours: 'Scale Contour',
  arrow: 'North Arrow',
  skeleton: 'Skeletons',
  status: 'Status'
};

const initialForm = (grave) => ({
  identifier: grave?.identifier || '',
  siteId: String(grave?.siteId ?? ''),
  depositionType: String(grave?.depositionType ?? ''),
  internmentType: String(grave?.internmentType ?? ''),
  percentageScale: grave?.percentageScale ? `1:${grave.percentageScale}` : '',
  pageSize: String(grave?.pageSize ?? ''),
  selectedTagIds: grave?.tags?.map((tag) => tag.id) || [],
  verified: Boolean(grave?.verified)
});

const createSavers = ({
  onUpdateGrave,
  onUpdateTags,
  onUpdateScale,
  onUpdateSkeletons,
  onUpdateArrow,
  onRecalculate
}) => ({
  data: (values) =>
    onUpdateGrave({
      identifier: values.identifier,
      siteId: parseIntOrNull(values.siteId),
      depositionType: parseIntOrNull(values.depositionType),
      internmentType: parseIntOrNull(values.internmentType)
    }),
  site: (values) => onUpdateGrave({ siteId: parseIntOrNull(values.siteId) }),
  tags: (values, data) => onUpdateTags(data.tagIds ?? values.selectedTagIds),
  scale: (values, data) => onUpdateScale(data),
  skeleton: (values, data) =>
    data.skeletonDepositions &&
    onUpdateSkeletons(
      Object.entries(data.skeletonDepositions).map(([skeletonId, type]) => ({ skeletonId, type }))
    ),
  arrow: (values, data) => data.arrowId && onUpdateArrow(data.arrowId, data.angle),
  resize_boxes: () => onRecalculate?.(),
  status: (values) => onUpdateGrave({ verified: values.verified })
});

export default function GraveWizard({
  step,
  setStep,
  grave,
  sites,
  tags,
  onCancel,
  onFinish,
  loading,
  ...handlers
}) {
  const hasSkeletons = grave?.skeletonFigures?.length > 0;
  const hasScale = Boolean(grave?.scale);
  const steps = STEP_ORDER.filter(
    (name) => (name !== 'skeleton' || hasSkeletons) && (name !== 'show_scale_contours' || hasScale)
  );
  const { formData, updateField, handleNext } = useWizardFlow({
    steps,
    step,
    setStep,
    initialData: initialForm(grave),
    savers: createSavers(handlers)
  });

  const handleFinishFigures = async (figures) => {
    await handlers.onUpdateFigures(figureUpdateInputs(figures));
    handleNext();
  };

  const renderStep = () => {
    switch (step) {
      case 'data':
        return (
          <DataStep
            grave={grave}
            state={formData}
            onNext={handleNext}
            onCancel={onCancel}
            loading={loading}
          />
        );
      case 'site':
        return (
          <SiteStep
            grave={grave}
            state={formData}
            sites={sites}
            onNext={handleNext}
            onCancel={onCancel}
            loading={loading}
          />
        );
      case 'tags':
        return (
          <TagsStep
            tags={tags}
            selectedTagIds={formData.selectedTagIds}
            onSelectTag={(ids) => updateField('selectedTagIds', ids)}
            onNext={handleNext}
            onCancel={onCancel}
          />
        );
      case 'resize_boxes':
        return (
          <ResizeBoxesStep
            resource={grave}
            sites={sites}
            message="Resize bounding boxes and create new boxes for objects related to this burial."
            onFinishFigures={handleFinishFigures}
            onCancel={onCancel}
          />
        );
      case 'show_contours':
        return <ShowContoursStep grave={grave} onNext={handleNext} onCancel={onCancel} />;
      case 'scale':
        return (
          <ScaleStep
            resource={grave}
            typeLabel="Grave"
            state={formData}
            onNext={handleNext}
            onCancel={onCancel}
            loading={loading}
          />
        );
      case 'show_scale_contours':
        return (
          <ShowScaleContoursStep
            resource={grave}
            onUpdateScaleContour={handlers.onUpdateScaleContour}
            onNext={handleNext}
            onCancel={onCancel}
          />
        );
      case 'arrow':
        return (
          <ArrowStep grave={grave} arrow={grave.arrow} onNext={handleNext} onCancel={onCancel} />
        );
      case 'skeleton':
        return hasSkeletons ? (
          <SkeletonStep skeletons={grave.skeletonFigures} onNext={handleNext} onCancel={onCancel} />
        ) : null;
      case 'status':
        return (
          <StatusStep
            resource={grave}
            state={formData}
            updateField={updateField}
            onNext={handleNext}
            onCancel={onCancel}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <WizardShell
      steps={steps}
      labels={STEP_LABELS}
      step={step}
      setStep={setStep}
      onFinish={onFinish}
    >
      {renderStep()}
    </WizardShell>
  );
}
